// src/components/SetupTemplateManager.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection, query, where, onSnapshot,
  addDoc, deleteDoc, doc, orderBy, updateDoc, deleteField,
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, IconButton, CircularProgress,
  Paper, Button, List, ListItem, ListItemText,
  ListItemIcon, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';

import TemplateEditor from './setup_car_centric/TemplateEditor';

// ── COMPONENTE PRINCIPALE ─────────────────────────────────────────

function SetupTemplateManager({ teamId, userRole }) {
  const { t } = useTranslation('setup_template');

  const [templates, setTemplates]       = useState([]);
  const [isLoading, setIsLoading]       = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = lista, 'new' | {template} = editor
  const canManage = userRole === 'admin' || userRole === 'Engineer';

  useEffect(() => {
    if (!teamId) return;
    const q = query(
      collection(db, 'setupTemplates'),
      where('teamId', '==', teamId),
      orderBy('templateName', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    }, (err) => {
      console.error('Error fetching templates:', err);
      setIsLoading(false);
    });
    return () => unsub();
  }, [teamId]);

  const handleDelete = async (templateId) => {
    if (window.confirm(t('delete_confirm'))) {
      try {
        await deleteDoc(doc(db, 'setupTemplates', templateId));
      } catch (err) {
        console.error('Error deleting template:', err);
        alert(t('delete_error'));
      }
    }
  };

  const handleSaveTemplate = async (templateId, templateName, modules) => {
    if (templateId) {
      await updateDoc(doc(db, 'setupTemplates', templateId), {
        teamId,
        templateName,
        modules,
        sections: deleteField(), // rimuovi vecchio campo se presente
      });
    } else {
      await addDoc(collection(db, 'setupTemplates'), { teamId, templateName, modules });
    }
    setEditingTemplate(null);
  };

  // ── EDITOR VIEW ───────────────────────────────────────────────

  if (editingTemplate !== null) {
    return (
      <TemplateEditor
        template={editingTemplate === 'new' ? null : editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => setEditingTemplate(null)}
        t={t}
      />
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────

  return (
    <>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('subtitle')}
        </Typography>
      </Paper>

      {/* Nuovo template */}
      {canManage && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            onClick={() => setEditingTemplate('new')}
            sx={{ height: 48 }}
          >
            {t('add_button_new', { defaultValue: 'Nuovo Template' })}
          </Button>
        </Paper>
      )}

      {/* Lista template */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Typography variant="h5" gutterBottom>
          {t('existing_title', { defaultValue: 'Template esistenti' })}
        </Typography>

        {isLoading && <CircularProgress size={24} />}

        {!isLoading && templates.length === 0 && (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {t('empty', { defaultValue: 'Nessun template creato.' })}
          </Typography>
        )}

        {!isLoading && templates.length > 0 && (
          <List dense disablePadding>
            {templates.map((tpl) => {
              const moduleLabels = tpl.modules
                ? tpl.modules.map(m => t(`modules.module_${m.key}`, { defaultValue: m.key }))
                : Object.keys(tpl.sections || {}).filter(k => tpl.sections[k]).map(k => k);

              return (
                <ListItem
                  key={tpl.id}
                  divider
                  sx={{ backgroundColor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1 }}
                  secondaryAction={
                    canManage ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => setEditingTemplate(tpl)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(tpl.id)}>
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    ) : null
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <DescriptionIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={tpl.templateName}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                    secondary={
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pt: 0.5 }}>
                        {moduleLabels.length > 0
                          ? moduleLabels.map(name => <Chip key={name} label={name} size="small" />)
                          : <Chip label={t('needs_migration', { defaultValue: 'Da migrare' })} color="warning" size="small" />
                        }
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>
    </>
  );
}

export default SetupTemplateManager;
